import { IconButton, Stack, Tooltip } from "@mui/material";
import CallSplitIcon from "@mui/icons-material/CallSplit";
import InfoIcon from "@mui/icons-material/Info";
import BoltIcon from "@mui/icons-material/Bolt";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { AutoTransactionSuggestionButton } from "../AutoTransactionSuggestionButton";
import { CreateRuleButton } from "../CreateRuleButton";
import type {
  AutoTransactionRuleDto,
  TransactionId,
  TransactionListDto,
} from "../../api/types";

export type RowActionButtonsProps = {
  transaction: TransactionListDto;
  matchingRules: AutoTransactionRuleDto[] | undefined;
  onCreateRule: (tx: TransactionListDto) => void;
  onOpenSuggestions: (
    event: React.MouseEvent<HTMLElement>,
    txId: TransactionId,
  ) => void;
  onOpenAppliedRules: (
    event: React.MouseEvent<HTMLElement>,
    tx: TransactionListDto,
  ) => void;
  /// Desktop-only actions; the mobile card omits them (tapping the card
  /// already opens the details, and splitting is a details-page job there).
  onViewDetails?: (id: TransactionId) => void;
  onSplit?: (tx: TransactionListDto) => void;
};

/// The per-row action cluster, shared between the desktop actions cell and
/// the mobile card.
export const RowActionButtons = ({
  transaction,
  matchingRules,
  onCreateRule,
  onOpenSuggestions,
  onOpenAppliedRules,
  onViewDetails,
  onSplit,
}: RowActionButtonsProps) => (
  <Stack
    direction="row"
    spacing={onViewDetails ? 1 : 0.5}
    sx={{ justifyContent: "center" }}
  >
    {onViewDetails && (
      <Tooltip title="View Details">
        <IconButton
          aria-label="View Details"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onViewDetails(transaction.id);
          }}
        >
          <InfoIcon />
        </IconButton>
      </Tooltip>
    )}
    <CreateRuleButton
      transaction={transaction}
      onCreateRule={onCreateRule}
      size="small"
    />
    {onSplit && (
      <Tooltip title="Split Transaction">
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onSplit(transaction);
          }}
        >
          <CallSplitIcon />
        </IconButton>
      </Tooltip>
    )}
    {/* A rule reached a dispatch none of its arms named. Marked here so it is
        visible while scanning; the details page names the rule and links to
        it. On mobile the marker is passive — tapping the card opens the
        details, which explains it. */}
    {transaction.hasMatchProblem &&
      (onViewDetails ? (
        <Tooltip title="A rule couldn't decide what to do here — open the transaction to see which">
          <IconButton
            aria-label="a rule couldn't dispatch this transaction"
            size="small"
            color="warning"
            onClick={(e) => {
              e.stopPropagation();
              onViewDetails(transaction.id);
            }}
          >
            <WarningAmberIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <Tooltip title="A rule couldn't decide what to do here">
          <WarningAmberIcon
            fontSize="small"
            color="warning"
            aria-label="a rule couldn't dispatch this transaction"
          />
        </Tooltip>
      ))}
    {transaction.appliedRules.length > 0 && (
      <Tooltip
        title={
          transaction.appliedRules.some((r) => r.broken)
            ? "An applied rule is broken or deleted"
            : transaction.appliedRules.length === 1
              ? `Applied Rule: ${transaction.appliedRules[0].name}`
              : "Multiple Applied Rules"
        }
      >
        <IconButton
          size="small"
          color={
            transaction.appliedRules.some((r) => r.broken) ? "error" : "default"
          }
          onClick={(e) => {
            e.stopPropagation();
            onOpenAppliedRules(e, transaction);
          }}
        >
          <BoltIcon />
        </IconButton>
      </Tooltip>
    )}
    <AutoTransactionSuggestionButton
      transactionId={transaction.id}
      rules={matchingRules}
      onOpenSuggestions={onOpenSuggestions}
      size="small"
    />
  </Stack>
);
